import { useUndoWindow } from '@/components/companion/feed/use-undo-window';
import { Button, HStack, Text } from '@chakra-ui/react';
import type { CompanionDismissReason, CompanionUndoOfferDto } from '@fluenceur/types';
import { useTranslation } from 'react-i18next';

/**
 * What the companion recorded, in the place the card was.
 *
 * The wording states what was written down and stops there. It never says the
 * companion learned anything (no threshold moves in this version) and never
 * claims the work was done — "already handled" is the brand's own declaration.
 *
 * The undo button exists only when the server handed back an offer, which it
 * does for the person who gave the answer and for nobody else. A colleague
 * seeing the same feed simply finds the card gone.
 */

interface CompanionDismissedNoticeProps {
  reason: CompanionDismissReason;
  /** The delay the server actually granted, for the "later" answer only. */
  snoozeDays: number | null;
  undo: CompanionUndoOfferDto | null;
  onUndo: () => void;
  isUndoing: boolean;
}

export const CompanionDismissedNotice = ({
  reason,
  snoozeDays,
  undo,
  onUndo,
  isUndoing,
}: CompanionDismissedNoticeProps) => {
  const { t } = useTranslation();
  const canUndo = useUndoWindow(undo?.expiresAt);

  const message =
    reason === 'later'
      ? t('companion.panel.feedback.later', { count: snoozeDays ?? 0 })
      : t(`companion.panel.feedback.${reason}`);

  return (
    <HStack
      gap="12px"
      align="center"
      justify="space-between"
      px="18px"
      py="16px"
      bg="softCream"
      data-testid="companion-dismissed-notice"
      data-reason={reason}
    >
      <Text fontSize="13.5px" color="inkMuted" lineHeight="1.45">
        {message}
      </Text>
      {undo && canUndo ? (
        <Button
          variant="plain"
          h="auto"
          px="0"
          flexShrink={0}
          fontFamily="heading"
          fontSize="13.5px"
          fontWeight="600"
          color="ink"
          textDecoration="underline"
          _hover={{ color: 'inkMuted' }}
          onClick={onUndo}
          loading={isUndoing}
        >
          {t('companion.panel.dismiss.undo')}
        </Button>
      ) : null}
    </HStack>
  );
};
